import { addDays, endOfDay, endOfMonth, startOfDay } from 'date-fns'
import {
  Suspense,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { TopBar } from './components/TopBar'
import { FilterDrawer, type FeaturedOption } from './components/FilterDrawer'
import { CalendarView } from './components/CalendarView'
import { SubscribeForm } from './components/SubscribeForm'
import { FilterBanner } from './components/FilterBanner'
import { SiteFooter } from './components/SiteFooter'
import { BottomTabs } from './components/BottomTabs'
import { OnboardingModal, ONBOARDING_KEY } from './components/OnboardingModal'
import { onOnboardingDone } from './lib/onboardingBridge'
import { FeedbackWidget } from './components/FeedbackWidget'
import { usePendingStories } from './lib/useStories'
import { deleteStoryPhotos } from './lib/storyPhotos'
import { useRoute } from './lib/route'
import { useDocumentHead } from './lib/head'
import {
  hostedByCounts,
  hostedByNames,
  useApprovedEvents,
  usePendingEvents,
} from './lib/useEvents'
import { matchesQuery, suggestSearch } from './lib/search'
import { pickSearchHints } from './lib/searchHints'
import { matchesGroup, useFilterGroup, useLiveGroups } from './lib/useFilterGroup'
import {
  matchesSeasonal,
  seasonalAsGroup,
  seasonalBySlug,
  seasonalFilters,
} from './lib/seasonal'
import { useAuth } from './lib/auth'
import { supabase } from './lib/supabase'
import { track } from './lib/analytics'
import { AREAS, areaCounts, areaForVenue, type Area } from './lib/areas'
import { isPastEvent } from './lib/dates'
import { hostLabel } from './lib/hostLabels'
import { DISASTER_SUPPORT_SLUG } from './lib/disasterResources'
import { eventKinds, hasAnyKind } from './lib/kinds'
import { FAVORITES_KIND, useFavorites } from './lib/favorites'
import {
  POPULAR_KIND,
  collapseToNextOccurrence,
  popularKey,
  rankPopular,
  useEventPeople,
} from './lib/popularity'
import { lazyWithReload } from './lib/lazyWithReload'
import { isPick } from './lib/picks'
import {
  readEventId,
  readSearchQuery,
  readUrlFilters,
  resolveHostSlugs,
  resolveHostedBySlugs,
  slugify,
  writeSearchQuery,
  writeUrlFilters,
} from './lib/urlFilters'
import type { CostFilter, CostType, EventRow, HostCount, ViewMode } from './types'

// Tailwind's md and lg breakpoints. md (768px) is where the calendar stops
// rendering a single day; lg (1024px) is where the filter drawer stops being an
// overlay. Both are read once, for the initial state only.
const MD_QUERY = '(min-width: 768px)'
const LG_QUERY = '(min-width: 1024px)'

const isDesktopWidth = (query: string): boolean =>
  typeof window !== 'undefined' && window.matchMedia(query).matches

// Loaded on demand, not in the first bundle. Until 2026-09-23 everything below
// shipped in the one index-*.js every visitor downloads before the calendar can
// paint, and PageSpeed measured mobile LCP at 8.2 s with 185 KB of that JS never
// run on first load. None of these can be on screen at first paint: the modals
// open on a click, the approval queue is admin only, and the four pages are
// other routes. SubmitModal and ApprovalQueue alone are 2,500 lines.
//
// What stays eager, on purpose: CalendarView and FilterDrawer (the first paint
// itself; the drawer is open by default from lg), OnboardingModal (it IS the
// LCP element for a first visit, so a chunk request in front of it would make
// LCP later, not earlier) and FeedbackWidget (it renders its own launcher
// button while closed).
//
// The components are named exports, hence the .then() to hand lazy() a default.
// lazyWithReload reloads the tab once when a chunk is gone after a deploy, which
// used to blank the screen (lib/lazyWithReload).
const SubmitModal = lazyWithReload(() =>
  import('./components/SubmitModal').then((m) => ({ default: m.SubmitModal })),
)
const ApprovalQueue = lazyWithReload(() =>
  import('./components/ApprovalQueue').then((m) => ({ default: m.ApprovalQueue })),
)
const LoginModal = lazyWithReload(() =>
  import('./components/LoginModal').then((m) => ({ default: m.LoginModal })),
)
const SubscribeModal = lazyWithReload(() =>
  import('./components/SubscribeModal').then((m) => ({ default: m.SubscribeModal })),
)
const DisasterResourcesModal = lazyWithReload(() =>
  import('./components/DisasterResourcesModal').then((m) => ({
    default: m.DisasterResourcesModal,
  })),
)
const StoriesPage = lazyWithReload(() =>
  import('./components/StoriesPage').then((m) => ({ default: m.StoriesPage })),
)
const GroupsPage = lazyWithReload(() =>
  import('./components/GroupsPage').then((m) => ({ default: m.GroupsPage })),
)
const ResourcesPage = lazyWithReload(() =>
  import('./components/ResourcesPage').then((m) => ({ default: m.ResourcesPage })),
)
const VendorsPage = lazyWithReload(() =>
  import('./components/VendorsPage').then((m) => ({ default: m.VendorsPage })),
)

/**
 * True from the first time `open` is true, and stays true. A lazy modal is not
 * mounted until then, so a visitor who never opens it never downloads it. It
 * stays mounted after closing, which keeps each modal's behaviour exactly what
 * it was when all of them were mounted from the start: they return null while
 * closed and run their own reset-on-close effects, and those effects need the
 * component alive to see open go false.
 */
function useOpenedOnce(open: boolean): boolean {
  const [opened, setOpened] = useState(open)
  // Setting state during render is React's documented pattern for state derived
  // from a prop; it re-renders before commit, so there is no extra paint.
  if (open && !opened) setOpened(true)
  return opened
}

export default function App() {
  const { events, loading, settled, snapshotRange, refetch } = useApprovedEvents()
  // Starred on this device, no login (lib/favorites). Read here so both the
  // matched filter and the drawer's count move with it live.
  const { favorites: favoritedIds } = useFavorites()
  // event id -> people who opened it in the last 45 days (lib/popularity).
  // Fetched once; ranked into `popularOpens` below, once `today` exists.
  const eventPeople = useEventPeople()
  const { isAdmin, user, signOut } = useAuth()
  const { pending, refetch: refetchPending } = usePendingEvents(isAdmin)
  const { pendingStories, refetch: refetchPendingStories } =
    usePendingStories(isAdmin)
  // The calendar and Your Stories, as real paths (see lib/route).
  const [route, goTo] = useRoute()
  // Title, description, canonical and the events ItemList follow the route.
  // Passing the UNFILTERED events on purpose: the structured data describes what
  // this calendar carries, not whatever the current visitor happens to have
  // narrowed it to.
  useDocumentHead(route, events)

  // Filters can arrive pre-set from the URL (a link pasted into a Reddit/FB reply).
  const [initialUrlFilters] = useState(readUrlFilters)
  // A single event can arrive the same way (?event=<id>), read once on mount
  // the same as the filters above.
  const [initialEventId] = useState(readEventId)

  // Group links default to the rolling 7-day-from-today view: it shows the soonest
  // matching events regardless of weekday, which a 3-day window can miss entirely
  // for a narrow group. Everyone else opens on 3 days.
  // Mobile renders ONE day whatever the mode and both span buttons are desktop-
  // only, so phones open on 'rolling' - the value behind the toggle's single 'Day'
  // button, so the control has something selected. An explicit ?view= still wins,
  // including a legacy ?view=week, which urlFilters maps to 'rolling' on the way in.
  const [view, setView] = useState<ViewMode>(() => {
    if (initialUrlFilters.view) return initialUrlFilters.view
    if (initialUrlFilters.group) return 'rolling'
    return isDesktopWidth(MD_QUERY) ? '3day' : 'rolling'
  })
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date())
  // Open on desktop, closed below lg. From lg the drawer is an in-flow column that
  // pushes the calendar across; below lg it's an overlay with a scrim, so opening it
  // by default would hand a phone visitor a filter panel instead of the calendar.
  // Almost nobody found it on their own (60 days of PostHog: 6 type-toggles by 3
  // people), which is what this is for.
  const [filterOpen, setFilterOpen] = useState(() => isDesktopWidth(LG_QUERY))
  const [submitOpen, setSubmitOpen] = useState(false)
  const [approvalOpen, setApprovalOpen] = useState(false)
  const [loginOpen, setLoginOpen] = useState(false)
  const [subscribeOpen, setSubscribeOpen] = useState(false)
  // Lifted out of FeedbackWidget so the bottom tab can open it. The widget still
  // works uncontrolled anywhere else.
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  // The phone-only slot in TopBar that CalendarView portals its controls into.
  // State rather than a ref, so CalendarView re-renders once the node exists.
  const [controlsSlot, setControlsSlot] = useState<HTMLDivElement | null>(null)
  const [disasterResourcesOpen, setDisasterResourcesOpen] = useState(false)
  const submitMounted = useOpenedOnce(submitOpen)
  const approvalMounted = useOpenedOnce(approvalOpen)
  const loginMounted = useOpenedOnce(loginOpen)
  const subscribeMounted = useOpenedOnce(subscribeOpen)
  const disasterResourcesMounted = useOpenedOnce(disasterResourcesOpen)
  // First-visit interest picker (Andrew, 7/28). One localStorage flag per device:
  // submit, X, backdrop, Esc, and "show me everything" all set it, so the modal
  // never comes back. Visitors on a pre-filtered link (?group=/?hosts=/?q=) skip
  // it entirely - they already chose.
  const [onboardingOpen, setOnboardingOpen] = useState(() => {
    if (typeof window === 'undefined') return false
    const arrived =
      initialUrlFilters.group !== null ||
      initialUrlFilters.hostSlugs.length > 0 ||
      initialUrlFilters.hostedBySlugs.length > 0 ||
      initialUrlFilters.cost !== 'all' ||
      initialUrlFilters.areaSlugs.length > 0 ||
      readSearchQuery() !== ''
    // A live #boot-picker means main.tsx hydrated the shell's copy, which is
    // the one on screen; opening this one too would stack a second picker.
    if (document.getElementById('boot-picker')) return false
    return !arrived && !window.localStorage.getItem(ONBOARDING_KEY)
  })
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [cost, setCost] = useState<CostFilter>(initialUrlFilters.cost)
  const [groupSlug, setGroupSlug] = useState<string | null>(initialUrlFilters.group)
  // Seeded from ?q= so a shared search link opens with the search already run
  // (lib/urlFilters), which also flips the view to the agenda via the effect below.
  const [query, setQuery] = useState(readSearchQuery)
  // The input's OWN displayed value is always `query` - never this - so typing
  // is never held up by anything downstream. Everything that FILTERS off the
  // query (matched, searchSuggestions, the no-matches hint) reads this instead:
  // React keeps rendering the keystroke itself at full priority and lets the
  // expensive re-filter over the whole loaded set catch up a beat behind,
  // rather than making the keystroke wait on it. (2026-09-04, Andrew: "it's
  // difficult to type, it's really slow" - the filter and the typeahead were
  // both recomputing over every loaded row inside the SAME render that drew
  // the keystroke; see lib/search.ts for the other half of this fix.)
  const deferredQuery = useDeferredValue(query)

  // Typing a search switches the calendar to the agenda, and clearing it puts
  // the previous view back.
  //
  // WHY. A search narrows the set to a handful of events scattered across the
  // window, and a 3-day or 7-day grid then renders mostly empty columns with one
  // card somewhere in them - the matches are there, they are just spread across
  // a layout built for a full day. The agenda is one column of every match in
  // time order, which is the shape a result list wants.
  //
  // Only on the EDGES, and deliberately not keyed on `view`. Reacting to every
  // view change would re-force the agenda the moment someone switched away from
  // it mid-search, which is the user being overruled by their own typing.
  const wasSearching = useRef(false)
  const viewBeforeSearch = useRef<ViewMode | null>(null)
  useEffect(() => {
    const searching = query.trim().length > 0
    if (searching && !wasSearching.current) {
      if (view !== 'agenda') {
        viewBeforeSearch.current = view
        setView('agenda')
      }
    } else if (!searching && wasSearching.current) {
      if (viewBeforeSearch.current) {
        setView(viewBeforeSearch.current)
        viewBeforeSearch.current = null
      }
    }
    wasSearching.current = searching
    // `view` is read but must not re-trigger this - see the comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])
  // Empty = every area, matching how an empty host selection means every type.
  // Seeded from ?area= (lib/urlFilters). The area list is fixed, so unlike the
  // host slugs this resolves at once; an unknown slug is simply dropped, the
  // same "degrade to everything" rule a stale ?hosts= link follows.
  const [selectedAreas, setSelectedAreas] = useState<Set<Area>>(
    () => new Set(AREAS.filter((a) => initialUrlFilters.areaSlugs.includes(slugify(a)))),
  )
  // Who is putting the event on, keyed by the name hostedByName derives from the
  // venue. Empty = everyone.
  const [selectedHostedBy, setSelectedHostedBy] = useState<Set<string>>(new Set())
  // Andrew's Picks. Off = every event, the same as an empty selection anywhere
  // else in the drawer; there is no "only the ones he didn't pick".
  const [picksOnly, setPicksOnly] = useState(false)
  // Events that have already finished. OFF by default (Andrew, 7/29): the fetch
  // has never had a date floor, so an 11am market sat on the day view all
  // evening and last week's days were still full of things nobody could go to.
  // Kept as a switch rather than a hard cut because the calendar is also a
  // record of what this town runs - "was that market on Saturday" is a fair
  // question, and the answer is two taps away instead of gone.
  const [showPast, setShowPast] = useState(false)

  // Holidays and First Friday are computed, not stored, so they're resolved
  // before the table is consulted - and when one matches, the fetch is skipped
  // entirely rather than looking up a slug that was never a row. Pinned to the
  // first render so the windows can't shift under a session left open overnight.
  const [today] = useState(() => new Date())
  const activeSeasonal = useMemo(
    () => seasonalBySlug(groupSlug, today),
    [groupSlug, today],
  )

  // What every count in the drawer is counted over. `today` doubles as the
  // clock here - it is `new Date()` pinned at first render, and one pinned clock
  // is the point: a second one would let the checkbox counts and the calendar
  // disagree about which minute it is.
  //
  // The counts have to move with the toggle or the drawer lies. Before this,
  // Sports read 21 while the calendar could only ever show 17 of them, which is
  // the same advertise-7-deliver-6 wrongness the banner's upcomingCount already
  // exists to avoid. It also retires a category on its own: the four Watch Party
  // rows were all in the past, so the box was offering a filter that emptied the
  // calendar.
  // The Popular set, popularKey -> people: the top upcoming events by distinct
  // people (lib/popularity). Read here for the same reason favoritedIds is -
  // the matched filter, the drawer's count and the agenda's "(x views)" badge
  // all need it. Ranked over every loaded event, not the filtered set, so
  // ticking another filter never reshuffles what counts as popular.
  const popularOpens = useMemo(
    () => rankPopular(events, eventPeople, today),
    [events, eventPeople, today],
  )

  const scopedEvents = useMemo(
    () => (showPast ? events : events.filter((e) => !isPastEvent(e, today))),
    [events, showPast, today],
  )

  // An unknown slug comes back as null, which shows everything rather than an
  // empty calendar — these links outlive the groups they point at.
  const { group: savedGroup, nameRe } = useFilterGroup(
    activeSeasonal ? null : groupSlug,
  )

  // Memoized: this object is an effect dependency below, and a fresh one per
  // render would re-run the mirror on every keystroke.
  const seasonalGroup = useMemo(
    () => (activeSeasonal ? seasonalAsGroup(activeSeasonal) : null),
    [activeSeasonal],
  )
  const group = seasonalGroup ?? savedGroup

  const hostCounts = useMemo<HostCount[]>(() => {
    const m = new Map<string, number>()
    for (const e of scopedEvents) {
      // Counted under EVERY kind it carries (host + tags), so the Comedy box
      // says how many events a reader would see by ticking it - see lib/kinds.
      for (const h of eventKinds(e)) m.set(h, (m.get(h) ?? 0) + 1)
    }
    return [...m.entries()]
      .map(([host, count]) => ({ host, count }))
      // Sorted by the name on SCREEN, not the stored one: sorting by the raw
      // value puts "Farmer and Artisan Markets" down among the M's, because it's
      // filed under Market, and the list reads as though it isn't sorted at all.
      .sort((a, b) => hostLabel(a.host).localeCompare(hostLabel(b.host)))
  }, [scopedEvents])

  const allHosts = useMemo(() => hostCounts.map((h) => h.host), [hostCounts])

  // Every category the calendar has ever filed something under, past included.
  // The drawer's list is deliberately narrower - a box for a category with
  // nothing left to attend is a box that empties the calendar - but the SUBMIT
  // form has to keep offering all of them, or a category stops being
  // submittable in any week it happens to have nothing upcoming.
  const submitHosts = useMemo(() => {
    const seen = new Set<string>()
    for (const e of events) {
      if (e.host) seen.add(e.host)
      for (const t of e.tags ?? []) seen.add(t)
    }
    return [...seen].sort((a, b) => hostLabel(a).localeCompare(hostLabel(b)))
  }, [events])

  // Areas come out of the venue string rather than a column, so only the ones
  // that actually have events are offered (see lib/areas).
  const areas = useMemo(
    () => areaCounts(scopedEvents.map((e) => e.venue)),
    [scopedEvents],
  )

  // Every organiser name the data can produce, unthresholded and over ALL
  // events (past included): this is what ?hosted= slugs resolve against, and a
  // shared link should keep working for an organiser too small for a checkbox
  // or whose next event is a week out of the current scope.
  const allHostedByNames = useMemo(
    () => hostedByCounts(events, 1).map((h) => h.name),
    [events],
  )

  // Same venue string, read as WHO rather than where (plus any organiser
  // layered on top of it - lib/hostedBy's ORGANIZERS). Thresholded, because the
  // tail is 84 venues with a single event apiece - but the threshold reads a
  // venue's ALL-TIME rows while the count shows what is still ahead, so a
  // big-one-offs venue like the Amphitheater keeps its box between shows
  // (see lib/useEvents, Andrew 2026-08-04).
  //
  // Narrowed to the Location boxes (Andrew, 2026-09-27): with Bedford ticked,
  // Hosted by lists only who is putting something on in Bedford. And with an
  // area ticked there is no 3-event bar: that bar exists to keep 84 one-event
  // venues out of the whole-calendar list, but inside one area the list is
  // short, and a one-off host is exactly who someone browsing Appomattox wants
  // to find (Falling River Country Club, one golf classic, was missing).
  // An organiser that is already ticked stays listed even outside the areas,
  // or there would be no box to untick.
  const hostedBy = useMemo(() => {
    if (selectedAreas.size === 0) return hostedByCounts(scopedEvents, 3, events)
    const inAreas = scopedEvents.filter((e) => selectedAreas.has(areaForVenue(e.venue)))
    const list = hostedByCounts(inAreas, 1)
    const listed = new Set(list.map((h) => h.name))
    const kept = hostedByCounts(scopedEvents, 1).filter(
      (h) => selectedHostedBy.has(h.name) && !listed.has(h.name),
    )
    return [...list, ...kept].sort((a, b) => a.name.localeCompare(b.name))
  }, [scopedEvents, events, selectedAreas, selectedHostedBy])

  // Counted over the same set the Event type boxes count over, so the number
  // beside Picks means what the numbers beside its neighbours mean. Zero hides
  // the filter rather than offering a box that empties the calendar - the rule
  // the Featured chips already follow.
  const pickCount = useMemo(() => scopedEvents.filter(isPick).length, [scopedEvents])

  // Same rule for the Favorited Events row: how many of the CURRENTLY LOADED
  // events are starred on this device (lib/favorites), so the checkbox never
  // offers to filter to zero.
  const favoritedCount = useMemo(
    () => scopedEvents.filter((e) => favoritedIds.has(e.id)).length,
    [scopedEvents, favoritedIds],
  )

  // Distinct popular EVENTS, not rows: ticking Popular folds a multi-day
  // event to one row (see shownEvents below), so the count beside the checkbox
  // should say how many rows it will actually show, the same as every other
  // box in this section. Zero hides the row, same rule as Favorited and Picks.
  const popularCount = useMemo(() => {
    if (popularOpens.size === 0) return 0
    const keys = new Set<string>()
    for (const e of scopedEvents) {
      const key = popularKey(e)
      if (popularOpens.has(key)) keys.add(key)
    }
    return keys.size
  }, [scopedEvents, popularOpens])

  // Typeahead for the search box. Drawn from what the calendar actually holds,
  // so every suggestion returns something. Deferred query (see above) - the
  // list is allowed to land a beat behind the letters being typed, in exchange
  // for never being why one of those letters was slow to show up.
  const searchSuggestions = useMemo(
    () => suggestSearch(scopedEvents, deferredQuery),
    [scopedEvents, deferredQuery],
  )

  // Examples for the desktop search hint. Off `events`, not scopedEvents: the
  // hint says what the CALENDAR carries, so a visitor who has narrowed to one
  // neighborhood should still be told watercolor is a thing they can type.
  // Doesn't depend on the query, so it doesn't recompute while typing.
  const searchHints = useMemo(() => pickSearchHints(events, today), [events, today])

  // Featured chips: in their window AND with something upcoming to show. The
  // second half is the important one - a Halloween chip four weeks out, before
  // any Halloween event has been listed, would open an empty calendar, which is
  // the same bad first impression the jump-to-first-match effect exists to
  // prevent. Counting UPCOMING only, so it never advertises a number that
  // includes events already past.
  //
  // Curated rows with a window (✨ New!, and anything else rotating) go through
  // the same gate and sit in the same list. They're a different SOURCE - a
  // hand-written lyh_filter_groups row rather than a computed date - but to a
  // reader they're the same thing: a filter that's worth offering this week and
  // gone later, so one section holds both.
  const liveGroups = useLiveGroups(today)

  const featuredOptions = useMemo<FeaturedOption[]>(() => {
    const from = startOfDay(today)
    const upcoming = events.filter((e) => new Date(e.start_time) >= from)

    const seasonal: FeaturedOption[] = seasonalFilters(today).map((s) => ({
      slug: s.slug,
      label: s.label,
      count: upcoming.filter((e) => matchesSeasonal(e, s)).length,
    }))

    const curated: FeaturedOption[] = liveGroups.map((g) => {
      let re: RegExp | null = null
      try {
        re = g.name_match ? new RegExp(g.name_match, 'i') : null
      } catch {
        console.error('live group has an invalid name_match', g.slug)
      }
      return {
        slug: g.slug,
        label: g.label,
        count: upcoming.filter((e) => matchesGroup(e, g, re)).length,
      }
    })

    // The Disaster Support chip is the one featured option that can render at
    // zero events - it's a resources popup first, a filter second, so an admin
    // can turn it live before anything is tagged for it. Every other option
    // still needs at least one upcoming match, or a chip that empties the
    // calendar the moment it's touched would be the drawer's own worst first
    // impression (same reasoning as Andrew's Picks above).
    //
    // Pinned first with a stable sort rather than reordered into the curated/
    // seasonal arrays, so it stands out from "whatever's currently live" the
    // way Lisa asked - regardless of what else happens to be running.
    return [...curated, ...seasonal]
      .filter((o) => o.count > 0 || o.slug === DISASTER_SUPPORT_SLUG)
      .map((o) => ({ ...o, isDisaster: o.slug === DISASTER_SUPPORT_SLUG }))
      .sort((a, b) => (a.isDisaster === b.isDisaster ? 0 : a.isDisaster ? -1 : 1))
  }, [events, today, liveGroups])

  // Mirror the active group's HOST categories into the drawer's checkboxes
  // (Andrew, 7/28) - a group used to filter invisibly while the drawer showed
  // nothing checked. While a group is active the checkboxes are a REFLECTION,
  // not the filter (shownEvents skips the host filter then), because a group is
  // hosts UNION name_match and ANDing the mirrored hosts back in would drop the
  // name-matched events. Touching any box exits group mode and carries the
  // mirrored state forward as a plain host filter.
  useEffect(() => {
    if (!group) return
    setSelected(new Set(group.hosts.filter((h) => allHosts.includes(h))))
  }, [group, allHosts])

  // --- Linked event (?event=<id>) -------------------------------------------
  // Resolved against the RAW `events`, not shownEvents: a shared link should
  // open its event regardless of whatever host/cost/search filters also rode
  // along in the same URL - the same reasoning as ?hosts= links not requiring
  // a jump target to match every other filter. Runs on every `events` update,
  // so it can resolve off the CDN snapshot (lib/useEvents boots from that
  // first) without waiting on the slower Supabase pass.
  const linkedEventResolved = useRef(false)
  const [linkedEvent, setLinkedEvent] = useState<EventRow | null>(null)
  useEffect(() => {
    if (!initialEventId || linkedEventResolved.current) return
    const found = events.find((e) => e.id === initialEventId)
    if (found) {
      linkedEventResolved.current = true
      // Unknown, retired and past ids all degrade the same way: nothing
      // opens and the page is otherwise normal. These links sit in public
      // comments indefinitely, so a stale one must never error or blank
      // the calendar - the same rule urlFilters.ts enforces for filters.
      if (!isPastEvent(found, today)) setLinkedEvent(found)
      return
    }
    // `settled`, not `loading`: the CDN snapshot only covers 14 days back
    // and 90 forward, so a real id can still be waiting on the
    // authoritative Supabase pass.
    if (settled) linkedEventResolved.current = true
  }, [events, settled, initialEventId, today])

  // --- URL <-> filter sync -------------------------------------------------
  // Host slugs can only be matched once we know which hosts exist, so the first
  // fetch gates this. Until it resolves we never write, or an incoming link would
  // be clobbered by the empty default state before it was ever read.
  const urlSynced = useRef(false)

  // Both sync effects are gated on the calendar route. The filters belong to the
  // calendar's URL, and without this Your Stories would quietly accumulate
  // ?view=rolling&cost=free on a page where none of it means anything. `route`
  // is a dependency so stepping back onto the calendar restores the query string
  // from the state that was held the whole time.
  useEffect(() => {
    // `settled`, not `loading`: this resolves host slugs against the FULL set,
    // and the CDN snapshot only covers 14 days back and 90 forward.
    if (route !== 'calendar' || urlSynced.current || !settled) return
    const matchedHosts = resolveHostSlugs(initialUrlFilters.hostSlugs, allHosts)
    // Favorited Events rides the same ?hosts= list as a sentinel (lib/favorites),
    // not a real host - resolveHostSlugs only resolves against real ones, so
    // it's read back here as a special case alongside them. What drives the
    // FILTER is shareable; the favorites themselves stay per-device.
    const wantsFavorites = initialUrlFilters.hostSlugs.includes(slugify(FAVORITES_KIND))
    // Popular rides the same sentinel trick, same reason: resolveHostSlugs only
    // resolves against real hosts, so it's read back here alongside Favorited.
    const wantsPopular = initialUrlFilters.hostSlugs.includes(slugify(POPULAR_KIND))
    const matched = [
      ...matchedHosts,
      ...(wantsFavorites ? [FAVORITES_KIND] : []),
      ...(wantsPopular ? [POPULAR_KIND] : []),
    ]
    if (matched.length > 0) setSelected(new Set(matched))
    const matchedHostedBy = resolveHostedBySlugs(
      initialUrlFilters.hostedBySlugs,
      allHostedByNames,
    )
    if (matchedHostedBy.length > 0) setSelectedHostedBy(new Set(matchedHostedBy))
    urlSynced.current = true
    // Normalize on arrival: a slug that no longer matches any host is dropped
    // here, so an old link in a public comment degrades to "everything" rather
    // than rendering an empty calendar.
    writeUrlFilters({
      hostSlugs: matched.map(slugify),
      hostedBySlugs: matchedHostedBy.map(slugify),
      cost,
      view,
      group: groupSlug,
      areaSlugs: [...selectedAreas].map(slugify),
    })
  }, [route, settled, allHosts, allHostedByNames, initialUrlFilters, cost, view, groupSlug, selectedAreas])

  useEffect(() => {
    if (route !== 'calendar' || !urlSynced.current) return
    writeUrlFilters({
      // Mirrored-from-group hosts are presentation, not state - a shared ?group=
      // link stays clean rather than also carrying its own expansion.
      hostSlugs: groupSlug ? [] : [...selected].map(slugify),
      // Hosted-by is orthogonal to a group (see the filter comment below), so
      // unlike the host boxes it stays in the URL while a group is on.
      hostedBySlugs: [...selectedHostedBy].map(slugify),
      cost,
      view,
      group: groupSlug,
      areaSlugs: [...selectedAreas].map(slugify),
    })
  }, [route, selected, selectedHostedBy, cost, view, groupSlug, selectedAreas])


  const costCounts = useMemo<Record<CostType, number>>(
    () => ({
      free: scopedEvents.filter((e) => e.cost_type === 'free').length,
      ticketed: scopedEvents.filter((e) => e.cost_type === 'ticketed').length,
      promo: scopedEvents.filter((e) => e.cost_type === 'promo').length,
    }),
    [scopedEvents],
  )

  // Independent filters, all ANDed. Empty host selection = all types, empty area
  // selection = all areas, empty hosted-by = everyone. The cost match is exact,
  // so an event with cost_type === null drops out of every named filter instead
  // of being guessed into one.
  //
  // Search, area and hosted-by are orthogonal to a group, so unlike a host
  // checkbox none of them exits group mode - narrowing "date night" to Forest, or
  // to the word "trivia", is a sensible thing to want.
  //
  // Every filter EXCEPT the past cut, which is applied after: splitting it out
  // is what lets the toggle say how many events it is holding back, and lets an
  // empty past day own up to having had something on rather than claiming
  // nothing was ever listed.
  const matched = useMemo(() => {
    let out = events
    // Seasonal reads the note as well as the name, so it runs its own match
    // rather than matchesGroup - see the header of lib/seasonal for why the
    // saved groups must keep matching on the name alone.
    if (activeSeasonal) out = out.filter((e) => matchesSeasonal(e, activeSeasonal))
    else if (savedGroup) out = out.filter((e) => matchesGroup(e, savedGroup, nameRe))
    else if (selected.size > 0) {
      // Favorited Events and Popular both ride in this same set as sentinels
      // (lib/favorites, lib/popularity), not real hosts, so neither can go
      // through hasAnyKind/eventKinds. Both combine with the real kinds the
      // way two kinds combine - a union - so ticking Popular alongside Live
      // Music asks for "popular OR live music", not just the popular ones
      // that are also live music.
      const wantsFavorites = selected.has(FAVORITES_KIND)
      const wantsPopular = selected.has(POPULAR_KIND)
      const kinds = new Set(
        [...selected].filter((s) => s !== FAVORITES_KIND && s !== POPULAR_KIND),
      )
      out = out.filter(
        (e) =>
          (wantsFavorites && favoritedIds.has(e.id)) ||
          (wantsPopular && popularOpens.has(popularKey(e))) ||
          (kinds.size > 0 && hasAnyKind(e, kinds)),
      )
    }
    if (cost !== 'all') out = out.filter((e) => e.cost_type === cost)
    if (selectedAreas.size > 0)
      out = out.filter((e) => selectedAreas.has(areaForVenue(e.venue)))
    if (selectedHostedBy.size > 0)
      out = out.filter((e) => hostedByNames(e.venue, e.name).some((n) => selectedHostedBy.has(n)))
    // ANDed like everything else, even though it sits in the Event type section:
    // ticking Picks and Live Music asks for the picks that are live music, which
    // is the reading that matches every other pair of filters in the drawer.
    if (picksOnly) out = out.filter(isPick)
    // Someone searching "mahjong" wants the title, someone searching "Needle
    // Ninja" wants the place, the details are where a band or an act's name
    // usually lives, and the category catches "karaoke". Street addresses are
    // deliberately NOT matched - see lib/search. Deferred (see `deferredQuery`
    // above): this filter runs over EVERY loaded row, so it's the one this
    // whole memo exists to keep off the keystroke's own render.
    const q = deferredQuery.trim()
    if (q) out = out.filter((e) => matchesQuery(e, q))
    return out
  }, [
    events,
    selected,
    favoritedIds,
    popularOpens,
    cost,
    activeSeasonal,
    savedGroup,
    nameRe,
    selectedAreas,
    selectedHostedBy,
    picksOnly,
    deferredQuery,
  ])

  // One short label naming every active filter, feeding the empty-window hint
  // in CalendarView ("Nothing in these days for <this>."). Null when nothing is
  // filtering - that's also the hint's own on/off switch, since an unfiltered
  // empty window has no filter to name and keeps the plain "nothing listed"
  // copy instead. Reads `group` (seasonal or saved - see above) rather than the
  // mirrored host checkboxes, because while a group is active those checkboxes
  // are a REFLECTION of it, not a second filter, and listing both would name
  // the same narrowing twice.
  const activeFilterSummary = useMemo(() => {
    const parts: string[] = []
    if (selectedHostedBy.size > 0) parts.push([...selectedHostedBy].join(', '))
    if (group) parts.push(group.label)
    else if (selected.size > 0) {
      const wantsFavorites = selected.has(FAVORITES_KIND)
      const wantsPopular = selected.has(POPULAR_KIND)
      const kinds = [...selected].filter((s) => s !== FAVORITES_KIND && s !== POPULAR_KIND)
      if (wantsFavorites) parts.push('Favorited Events')
      if (wantsPopular) parts.push('Popular')
      if (kinds.length > 0) parts.push(kinds.map(hostLabel).join(', '))
    }
    if (selectedAreas.size > 0) parts.push([...selectedAreas].join(', '))
    if (picksOnly) parts.push("Andrew's Picks")
    if (cost !== 'all') parts.push(cost === 'free' ? 'Free' : cost === 'ticketed' ? 'Paid' : 'Deal')
    // deferredQuery, not query: this label names what `matched` actually
    // filtered on, which during a fast burst of typing is a beat behind the
    // box itself - naming the letter that hasn't been searched yet would read
    // as though it had.
    const q = deferredQuery.trim()
    if (q) parts.push(q)
    return parts.length > 0 ? parts.join(', ') : null
  }, [selectedHostedBy, group, selected, selectedAreas, picksOnly, cost, deferredQuery])

  // The two halves of that set, split on whether the thing has already ended.
  // hiddenPast is computed either way and is simply empty once the toggle is on,
  // so nothing downstream has to ask which mode it's in.
  const shownEvents = useMemo(() => {
    const upcoming = showPast ? matched : matched.filter((e) => !isPastEvent(e, today))
    // Popular folds a multi-day event to its soonest day (lib/popularity) -
    // done here, after the past cut, so "soonest" means soonest UPCOMING
    // rather than a day of the fair that is already over. Only entries
    // popularOpens knows about get folded, so a category ticked alongside
    // Popular still lists in full.
    return selected.has(POPULAR_KIND)
      ? collapseToNextOccurrence(upcoming, popularOpens)
      : upcoming
  }, [matched, showPast, today, selected, popularOpens])
  const hiddenPast = useMemo(
    () => (showPast ? [] : matched.filter((e) => isPastEvent(e, today))),
    [matched, showPast, today],
  )

  // What people TYPE is a demand signal no checkbox can give: a search that
  // returns nothing names something the calendar doesn't carry yet. The result
  // count rides along so the empty ones are findable.
  //
  // Fired on COMMIT - Enter, blur, or taking a suggestion - and never on a pause
  // in typing. It used to fire 800ms after the last keystroke, which logged a row
  // every time someone stopped to think: one search for karaoke arrived as "kar",
  // "karok", "karaok", "karaoke". That skews every total toward slow typers and
  // long words and makes a distinct-query count meaningless. A commit is the
  // moment the query is the thing they meant.
  //
  // Only the CAPTURE waits. The search itself is untouched and still narrows the
  // calendar on every keystroke.
  const [searchCommit, setSearchCommit] = useState<{ q: string; seq: number } | null>(null)
  const commitSearch = useCallback((q: string) => {
    setSearchCommit((prev) => ({ q, seq: (prev?.seq ?? 0) + 1 }))
  }, [])

  // Taking a suggestion sets the text and commits it in the same handler, so the
  // count is a render behind at the moment of the click. Waiting for the render
  // where the list reflects the committed query is what makes `results` true for
  // a picked suggestion rather than for whatever was half-typed before it. seq
  // (not the string) is the fired/not-fired key, so searching the same word twice
  // in a session is two rows, while an Enter followed by the blur it causes stays
  // one.
  const loggedSeq = useRef(0)
  useEffect(() => {
    if (!searchCommit || searchCommit.seq === loggedSeq.current) return
    const q = searchCommit.q.trim()
    if (!q) {
      loggedSeq.current = searchCommit.seq
      return
    }
    if (q !== query.trim()) return
    loggedSeq.current = searchCommit.seq
    track('search_used', { q, results: shownEvents.length })
  }, [searchCommit, query, shownEvents])

  // The search rides in ?q= (lib/urlFilters) so a search can be shared as a
  // link, but only once it is SUBMITTED (Andrew, 2026-09-25): the same commit
  // that logs search_used above (Enter, leaving the box, or taking a
  // suggestion), never per keystroke. Clearing the box removes it, so the URL
  // never names a search the page is no longer showing. Same calendar-route gate
  // as the filter sync.
  useEffect(() => {
    if (route !== 'calendar' || !searchCommit) return
    writeSearchQuery(searchCommit.q)
  }, [route, searchCommit])
  useEffect(() => {
    if (route === 'calendar' && !query.trim()) writeSearchQuery('')
  }, [route, query])

  // The fetch has no date floor, so shownEvents still carries past events. Counting
  // those would advertise 7 and deliver 6, which is exactly the small wrongness
  // that makes a shared link feel broken to someone arriving from Reddit.
  const upcomingCount = useMemo(() => {
    const from = startOfDay(new Date())
    return shownEvents.filter((e) => new Date(e.start_time) >= from).length
  }, [shownEvents])

  // Landing on a filtered link can put you on a day the filter excludes: a
  // weekend-only group opened on a Thursday rendered "Nothing listed for this day
  // yet" with 18 matching events sitting two days away. That's the worst possible
  // first impression for a link posted in a public thread, where the visitor has
  // no idea a filter is even involved.
  //
  // So jump ONCE to the first upcoming day that actually has matching events.
  // Once only, or navigating to a genuinely empty day would be impossible.
  const arrivedFiltered =
    initialUrlFilters.group !== null ||
    initialUrlFilters.hostSlugs.length > 0 ||
    initialUrlFilters.areaSlugs.length > 0 ||
    initialUrlFilters.cost !== 'all'
  const jumpedToFirstMatch = useRef(false)

  // The day to jump to, or null when the window on screen already shows a match
  // (or nothing matches at all).
  //
  // Only jump when the current view would show an EMPTY window. Rolling opens on
  // today, and a 7-day window usually already contains the soonest match, so we
  // leave the anchor on today; we jump only when nothing matches in view (e.g. a
  // single mobile day, or the next match is weeks out). This keeps "today is the
  // first column" for rolling while never stranding anyone on a blank screen.
  const findEmptyWindowJump = useCallback((): Date | null => {
    const from = startOfDay(new Date())
    const upcoming = shownEvents
      .map((e) => new Date(e.start_time))
      .filter((d) => d >= from)
      .sort((a, b) => a.getTime() - b.getTime())

    const first = upcoming[0]
    if (!first) return null

    // The agenda runs from the anchor forward with no end, so the soonest match
    // is always its first row - there is no empty window to rescue anyone from,
    // and jumping would only move the anchor off today for no gain.
    if (view === 'agenda') return null

    // Mobile is tested FIRST because a phone renders exactly one day in every
    // mode now, month included - CalendarView coerces it. Testing month first
    // measured a whole month of window against a screen showing a single day, so
    // a filtered link could conclude the match was already visible and strand
    // someone on an empty day, which is the precise thing this jump exists to
    // prevent.
    const mobile = window.matchMedia('(max-width: 767px)').matches
    const winStart = startOfDay(anchorDate)
    const winEnd = mobile
      ? endOfDay(anchorDate)
      : view === 'month'
        ? endOfMonth(anchorDate)
        : endOfDay(addDays(anchorDate, view === '3day' ? 2 : 6))

    const visible = upcoming.some((d) => d >= winStart && d <= winEnd)
    return visible ? null : startOfDay(first)
  }, [shownEvents, anchorDate, view])

  useEffect(() => {
    // Needs every row: the first match for a filtered link may be past the
    // snapshot's horizon, and jumping to the wrong day is worse than a beat's wait.
    if (jumpedToFirstMatch.current || !settled || !arrivedFiltered) return
    // A group arrives async; wait for it rather than jumping on the unfiltered set.
    if (initialUrlFilters.group !== null && !group) return

    jumpedToFirstMatch.current = true
    const target = findEmptyWindowJump()
    if (target) setAnchorDate(target)
  }, [settled, arrivedFiltered, group, findEmptyWindowJump, initialUrlFilters])

  // An onboarding pick gets the same treatment as arriving on a group link. A
  // pick on a quiet Tuesday otherwise renders "Nothing listed for this day yet"
  // with a dozen matches sitting later in the week, which on mobile - one day
  // filling the screen - reads as a filter that found nothing, on the very first
  // screen a new visitor sees.
  //
  // Holds the slug rather than a flag, so a second pick before the first group
  // resolves supersedes it instead of jumping on the wrong set.
  const jumpForGroup = useRef<string | null>(null)

  useEffect(() => {
    const want = jumpForGroup.current
    if (!want || !settled) return
    if (want !== groupSlug) {
      jumpForGroup.current = null
      return
    }
    if (!group) return // still fetching, or the group has no rules to filter by

    jumpForGroup.current = null
    const target = findEmptyWindowJump()
    if (target) setAnchorDate(target)
  }, [settled, groupSlug, group, findEmptyWindowJump])

  const toggleHost = (h: string) => {
    if (groupSlug) setGroupSlug(null) // manual filtering takes over from the group
    setSelected((prev) => {
      const n = new Set(prev)
      if (n.has(h)) n.delete(h)
      else n.add(h)
      return n
    })
  }
  // Popular gets its own toggle rather than routing through toggleHost, because
  // selecting it also carries a view change: a name-deduped shortlist reads as
  // a list, not a grid, so turning it ON switches to agenda. Turning it back
  // OFF restores whatever view was active before, the same "restore on the way
  // out" shape the search-triggers-agenda effect above uses - and for the same
  // reason: forcing everyone back to 3-day would overrule a view they picked
  // for themselves. Only restores when THIS toggle was what changed the view -
  // if agenda was already showing (say, from a search), there is nothing to
  // undo and leaving Popular's set leaves the view exactly where it was.
  const viewBeforePopular = useRef<ViewMode | null>(null)
  const restoreViewFromPopular = () => {
    if (viewBeforePopular.current) {
      setView(viewBeforePopular.current)
      viewBeforePopular.current = null
    }
  }
  //
  // Turning it ON also clears every other filter and the search (Andrew,
  // 2026-09-25): Popular is a shortlist of a handful of events, so ANDing it
  // with a cost, area or search almost always empties the list. It stays
  // combinable afterwards; this only resets on the way in.
  const togglePopular = () => {
    if (groupSlug) setGroupSlug(null)
    const turningOn = !selected.has(POPULAR_KIND)
    if (!turningOn) {
      setSelected((prev) => {
        const n = new Set(prev)
        n.delete(POPULAR_KIND)
        return n
      })
      restoreViewFromPopular()
      return
    }
    setSelected(new Set([POPULAR_KIND]))
    setCost('all')
    setSelectedAreas(new Set())
    setSelectedHostedBy(new Set())
    setPicksOnly(false)
    setShowPast(false)
    // Clearing the search would make the search effect restore the view it
    // saved on the way in, which lands AFTER the agenda switch below and undoes
    // it. So Popular takes over that saved view as its own restore point, and
    // the search effect finds nothing left to restore.
    const before = viewBeforeSearch.current ?? (view !== 'agenda' ? view : null)
    viewBeforeSearch.current = null
    setQuery('')
    if (before) viewBeforePopular.current = before
    if (view !== 'agenda') setView('agenda')
  }
  const toggleArea = (a: Area) => {
    setSelectedAreas((prev) => {
      const n = new Set(prev)
      if (n.has(a)) n.delete(a)
      else n.add(a)
      return n
    })
  }
  // The whole city at once. An explicit selecting flag rather than a per-area
  // toggle, or ticking Lynchburg while Downtown was already ticked would turn
  // Downtown back off.
  const toggleAreaGroup = (group: Area[], selecting: boolean) => {
    setSelectedAreas((prev) => {
      const n = new Set(prev)
      for (const a of group) {
        if (selecting) n.add(a)
        else n.delete(a)
      }
      return n
    })
  }
  const toggleHostedBy = (name: string) => {
    setSelectedHostedBy((prev) => {
      const n = new Set(prev)
      if (n.has(name)) n.delete(name)
      else n.add(name)
      return n
    })
  }
  const selectAll = () => {
    if (groupSlug) setGroupSlug(null)
    // Same reasoning as clearTypes/clear: Select all replaces the whole set,
    // Popular included, so it has to undo the same view switch.
    if (selected.has(POPULAR_KIND)) restoreViewFromPopular()
    setSelected(new Set(allHosts))
  }
  // The partner to Select all, and scoped like it: this empties the type boxes
  // and nothing else, where the header's Clear All resets the whole drawer.
  // Exits a group for the same reason Select all does - the boxes are a mirror
  // of the group while one is on, so editing them takes over from it.
  const clearTypes = () => {
    if (groupSlug) setGroupSlug(null)
    // Wipes Popular along with every real category, so undo its view switch
    // the same way its own checkbox would - see togglePopular above.
    if (selected.has(POPULAR_KIND)) restoreViewFromPopular()
    setSelected(new Set())
    setPicksOnly(false)
  }
  // Picking a Coming up chip. The host boxes are cleared either way: on the way
  // IN a seasonal filter has no categories of its own to mirror, and on the way
  // OUT a mirrored reflection would stay behind as a plain host filter, so
  // leaving the filter would not return you to the whole calendar.
  const selectGroup = (slug: string | null) => {
    if (slug) jumpForGroup.current = slug
    // Same reasoning as clearTypes/clear: a Featured chip also wipes the type
    // boxes, Popular included, so it has to undo the same view switch.
    if (selected.has(POPULAR_KIND)) restoreViewFromPopular()
    setSelected(new Set())
    setGroupSlug(slug)
  }
  // Clear All (drawer header) and the banner's Show all both land here. Reset
  // means reset: every filter, including the ones the drawer added.
  const clear = () => {
    if (groupSlug) setGroupSlug(null)
    // Same reasoning as clearTypes above: Clear All drops Popular too, so it
    // has to undo the same view switch or "show everything" would strand
    // someone on the agenda that was only ever Popular's.
    if (selected.has(POPULAR_KIND)) restoreViewFromPopular()
    setSelected(new Set())
    setCost('all')
    setSelectedAreas(new Set())
    setSelectedHostedBy(new Set())
    setPicksOnly(false)
    setQuery('')
    // Back to hidden, like every other filter goes back to its default. "Show
    // me everything" on this calendar has always meant everything still to
    // come; a Clear All that left last week's events on screen would be the one
    // control that didn't return to where it started.
    setShowPast(false)
  }

  // One pick applies the full group filter (banner + shareable ?group= URL).
  // Several picks union the groups' HOST categories into the checkbox filter -
  // the group URL carries one slug, and the drawer's Event type boxes make a
  // multi-pick visible and adjustable. Group host lists are fetched fresh so they
  // stay in sync with lyh_filter_groups.
  //
  // This is now the only in-app way into a group; the drawer's interest pills
  // were dropped on Lisa's second pass. Shared ?group= links and the Groups modal
  // still reach them from outside.
  const finishOnboarding = async (slugs: string[]) => {
    window.localStorage.setItem(ONBOARDING_KEY, '1')
    setOnboardingOpen(false)
    if (slugs.length === 0) return
    if (slugs.length === 1) {
      jumpForGroup.current = slugs[0]
      setGroupSlug(slugs[0])
      return
    }
    const { data } = await supabase
      .from('lyh_filter_groups')
      .select('hosts')
      .in('slug', slugs)
    const union = new Set<string>()
    for (const row of data ?? []) {
      for (const h of row.hosts ?? []) if (allHosts.includes(h)) union.add(h)
    }
    if (union.size > 0) setSelected(union)
  }
  // The shell's picker answers through the bridge (lib/onboardingBridge). A ref,
  // so the subscription is made once and still reaches this render's closure.
  const finishOnboardingRef = useRef(finishOnboarding)
  finishOnboardingRef.current = finishOnboarding
  useEffect(() => onOnboardingDone((slugs) => void finishOnboardingRef.current(slugs)), [])

  const approve = async (id: string) => {
    await supabase.from('lyh_events').update({ status: 'approved' }).eq('id', id)
    await Promise.all([refetch(), refetchPending()])
  }
  const reject = async (id: string) => {
    await supabase.from('lyh_events').update({ status: 'rejected' }).eq('id', id)
    await refetchPending()
  }

  // Stories carry no .select() on the way back for the same reason events don't:
  // the row leaves the admin's readable set the moment it stops being pending.
  const approveStory = async (id: string) => {
    await supabase.from('lyh_stories').update({ status: 'approved' }).eq('id', id)
    await refetchPendingStories()
  }
  const rejectStory = async (id: string) => {
    // Take the photos with it. Someone who sent us a picture and got turned down
    // should not have it sitting in a bucket afterwards. Read the paths BEFORE
    // the status write, because rejecting drops the row out of the admin's
    // pending set and the refetch below takes it off this list.
    const photos = pendingStories.find((s) => s.id === id)?.photos ?? []
    await supabase.from('lyh_stories').update({ status: 'rejected' }).eq('id', id)
    // Best effort, and after the status write: a delete that fails must not
    // leave the story pending and back in the queue.
    await deleteStoryPhotos(photos)
    await refetchPendingStories()
  }

  return (
    // h-full, not h-screen: `100vh` is the toolbar-HIDDEN height on iOS, which
    // pushed the footer and the tail of the list off screen. #root is now sized
    // in dvh (see index.css), so 100% of it tracks the visible viewport.
    // pb reserves the BottomTabs bar. The bar is `fixed`, so it sits outside this
    // column's flow and would otherwise overlay the footer and the tail of every
    // scrolling list. Padding the root instead of each scroll container means the
    // whole app ends above the bar and nothing individually has to know it exists.
    // 3.5rem covers the tallest form of the bar (icon 20 + gap 2 + 12px label +
    // py-2), and the env() term keeps it clear of the iOS home indicator too.
    // md:pb-0 because the bar itself is md:hidden - reserving space for a bar
    // that is not there would leave a dead strip under the desktop footer.
    <div className="flex h-full flex-col overflow-hidden bg-paper pb-[calc(3.5rem+env(safe-area-inset-bottom))] text-slate-900 md:pb-0 dark:bg-slate-950 dark:text-slate-100">
      <TopBar
        filterOpen={filterOpen}
        onToggleFilter={() => setFilterOpen((o) => !o)}
        onOpenGroups={() => goTo('groups')}
        onOpenSubmit={() => setSubmitOpen(true)}
        onOpenStories={() => goTo('stories')}
        onOpenCalendar={() => goTo('calendar')}
        onOpenVendors={() => goTo('vendors')}
        route={route}
        isAdmin={isAdmin}
        pendingCount={pending.length + pendingStories.length}
        onOpenApproval={() => setApprovalOpen(true)}
        userEmail={user?.email ?? null}
        onOpenLogin={() => setLoginOpen(true)}
        onSignOut={signOut}
        onOpenSubscribe={() => setSubscribeOpen(true)}
        onOpenDisasterResources={() => setDisasterResourcesOpen(true)}
        controlsSlotRef={setControlsSlot}
      />

      {/* The four surfaces. Only one is mounted at a time, so leaving the
          calendar stops its effects rather than running them behind a hidden
          panel; the filter state itself lives up here and survives the trip, so
          coming back lands on the same filters and the same day. */}
      {/* The four non-calendar pages are lazy (see the note above App). The
          fallback is an empty flex-1 box rather than a spinner: it holds the
          footer and bottom tabs where the page will put them, so nothing jumps
          when the chunk lands a moment later. */}
      {route !== 'calendar' ? (
        <Suspense fallback={<div className="min-h-0 flex-1" />}>
          {route === 'stories' ? (
            <StoriesPage onBack={() => goTo('calendar')} />
          ) : route === 'groups' ? (
            <GroupsPage onBack={() => goTo('calendar')} />
          ) : route === 'resources' ? (
            <ResourcesPage onBack={() => goTo('calendar')} />
          ) : (
            <VendorsPage events={events} settled={settled} />
          )}
        </Suspense>
      ) : (
        <>
          {group && (
            <FilterBanner group={group} shownCount={upcomingCount} onClear={clear} />
          )}

          <div className="relative flex min-h-0 flex-1">
            <FilterDrawer
              open={filterOpen}
              onClose={() => setFilterOpen(false)}
              hosts={hostCounts}
              selected={selected}
              onToggle={toggleHost}
              onSelectAll={selectAll}
              onClear={clear}
              onClearTypes={clearTypes}
              searchSuggestions={searchSuggestions}
              searchHints={searchHints}
              cost={cost}
              onChangeCost={setCost}
              costCounts={costCounts}
              query={query}
              onChangeQuery={setQuery}
              onCommitQuery={commitSearch}
              onSuggestionTaken={() => {
                // Below lg the drawer is an overlay covering the calendar (see the
                // breakpoint note in FilterDrawer), so on a phone a picked result
                // landed behind it and the tap looked like it did nothing. Close it
                // there. From lg the drawer is its own column beside the calendar,
                // so it stays open. Same breakpoint as the drawer's own lg: classes.
                if (window.matchMedia('(max-width: 1023px)').matches) setFilterOpen(false)
                // Typing already switches to agenda (the effect above), but not if
                // someone switched away mid-search, on purpose. A tap on a result is
                // a clearer request to see results than typing is, so it forces the
                // list, remembering the prior view so clearing the search restores it.
                if (view !== 'agenda') {
                  if (!viewBeforeSearch.current) viewBeforeSearch.current = view
                  setView('agenda')
                }
              }}
              noMatches={deferredQuery.trim().length > 0 && shownEvents.length === 0}
              areas={areas}
              selectedAreas={selectedAreas}
              onToggleArea={toggleArea}
              onToggleAreaGroup={toggleAreaGroup}
              featured={featuredOptions}
              groupSlug={groupSlug}
              onSelectGroup={selectGroup}
              onOpenDisasterResources={() => setDisasterResourcesOpen(true)}
              hostedBy={hostedBy}
              selectedHostedBy={selectedHostedBy}
              onToggleHostedBy={toggleHostedBy}
              pickCount={pickCount}
              picksOnly={picksOnly}
              onTogglePicks={() => setPicksOnly((p) => !p)}
              favoritedCount={favoritedCount}
              popularCount={popularCount}
              onTogglePopular={togglePopular}
            />

            {/* Below lg `main` is the single scroll container; from lg the calendar
                column scrolls itself. The right-hand "What people want more of"
                sidebar that used to sit here from lg up became the Insights modal on
                7/27 and was unwired entirely on 7/28, so the calendar has the full
                width at every size. The modal and its data were removed from the repo
                on 2026-10-10. */}
            <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto lg:overflow-hidden">
              <div className="flex min-w-0 flex-col px-2 pb-2 lg:min-h-0 lg:flex-1 lg:pt-2 lg:pr-3 lg:pl-3 lg:pb-3">
                <CalendarView
                  controlsSlot={controlsSlot}
                  events={shownEvents}
                  linkedEvent={linkedEvent}
                  hiddenPast={hiddenPast}
                  onShowPast={() => setShowPast(true)}
                  activeFilterSummary={activeFilterSummary}
                  view={view}
                  onChangeView={setView}
                  anchorDate={anchorDate}
                  onChangeAnchor={setAnchorDate}
                  onOpenSubmit={() => setSubmitOpen(true)}
                  filterOpen={filterOpen}
                  onToggleFilter={() => setFilterOpen((v) => !v)}
                  // While only the CDN snapshot is loaded, an EMPTY day is
                  // ambiguous: it may be a genuinely quiet day, or a date past
                  // the snapshot's 90-day horizon whose rows are still in
                  // flight. Keep the skeleton up for that case rather than
                  // telling someone nothing is on - the one message a visitor
                  // might act on by leaving.
                  loading={loading || (snapshotRange !== null && shownEvents.length === 0)}
                  // Only while Popular is on (see types.ts's popularOpens
                  // comment) - undefined the rest of the time is what keeps
                  // "(x views)" from ever showing up outside this filter.
                  popularOpens={selected.has(POPULAR_KIND) ? popularOpens : undefined}
                  subscribeSlot={
                    // Paper, not slate-50: this card sits INSIDE a white day cell,
                    // so it still needs to step away from its ground - but a cool
                    // grey was the one patch left that didn't belong to the warm
                    // palette. Paper on white reads as the same material.
                    <div className="rounded-lg border border-hairline bg-paper p-3 dark:border-slate-800 dark:bg-slate-900">
                      <p className="mb-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                        Get the week&apos;s events by email
                      </p>
                      <SubscribeForm variant="stacked" />
                    </div>
                  }
                />
              </div>
            </main>
          </div>
        </>
      )}

      {/* On a phone the open filter drawer covers the whole screen, and the
          footer is the one thing that isn't under it: a 49px strip sitting below
          the "Show events" button, which makes the panel look like it stopped
          short. Hidden while the drawer is open, and only at phone widths -
          from md the drawer is a panel again and the footer belongs on screen. */}
      <div className={`shrink-0 ${filterOpen ? 'hidden md:block' : ''}`}>
        <SiteFooter />
      </div>

      {/* Lazy, and mounted only once first opened (useOpenedOnce). Fallback is
          null: each of these is a fixed overlay, so there is no layout to hold,
          and the gap is one small chunk request after a click. */}
      <Suspense fallback={null}>
        {disasterResourcesMounted && (
          <DisasterResourcesModal
            open={disasterResourcesOpen}
            onClose={() => setDisasterResourcesOpen(false)}
          />
        )}
        {submitMounted && (
          <SubmitModal
            open={submitOpen}
            onClose={() => setSubmitOpen(false)}
            hosts={submitHosts}
            onSubmitted={() => refetchPending()}
          />
        )}
        {approvalMounted && (
          <ApprovalQueue
            open={approvalOpen}
            onClose={() => setApprovalOpen(false)}
            pending={pending}
            onApprove={approve}
            onReject={reject}
            stories={pendingStories}
            onApproveStory={approveStory}
            onRejectStory={rejectStory}
            refetchPending={refetchPending}
          />
        )}
        {loginMounted && <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />}
        {subscribeMounted && (
          <SubscribeModal open={subscribeOpen} onClose={() => setSubscribeOpen(false)} />
        )}
      </Suspense>
      <OnboardingModal open={onboardingOpen} onDone={finishOnboarding} />
      <FeedbackWidget open={feedbackOpen} onOpenChange={setFeedbackOpen} />

      {/* Fixed to the viewport bottom on every page and every width (Lisa,
          2026-09-07). The scroll containers above pay for it with bottom padding
          so the last row of a list is not sitting under the bar. */}
      <BottomTabs
        onSubscribe={() => setSubscribeOpen(true)}
        onFeedback={() => setFeedbackOpen(true)}
        onAddEvent={() => setSubmitOpen(true)}
      />
    </div>
  )
}
